// ============================================================
// FILE: BookingDetailContent.js
// LOCATION: C:\gardenmarket\web\src\pages\gardener\BookingDetailContent.js
// ============================================================
// PURPOSE:
//   Booking detail UI for the gardener side. Reused by
//   BookingDetail.js (standalone) and MyBookings.js (embedded).
//
// LAYOUT CHANGE:
//   Action buttons moved to DetailToolbar at top of pane,
//   matching JobDetailContent.js for a consistent look.
// ============================================================

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import client from '../../api/client';
import ReviewsTable from '../../components/ReviewsTable';
import PhotoUploader from '../../components/PhotoUploader';
import DetailToolbar from '../../components/DetailToolbar';
import { colors, radius, space, card, cardHeader, statusPill } from '../../theme/theme';

const gardenerStatusLabels = {
    open:'Open', matched:'Matched to you', quoted:'You quoted',
    accepted:'Booked', in_progress:'In progress',
    completed:'Completed', reviewed:'Reviewed',
};

export default function BookingDetailContent({ jobId, onJobChanged, embedded = false }) {
    const { user }  = useAuth();
    const navigate  = useNavigate();

    const [booking, setBooking] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState('');

    const loadBooking = async () => {
        const [b, r] = await Promise.all([
            client.get(`/jobs/${jobId}/booking-detail`),
            client.get(`/reviews/job/${jobId}`).catch(() => ({ data: { reviews: [] } })),
        ]);
        setBooking(b.data.booking);
        setReviews(r.data.reviews || []);
    };

    useEffect(() => {
        setLoading(true);
        setError('');
        loadBooking()
            .catch(err => setError(err.response?.data?.error || 'Failed to load booking.'))
            .finally(() => setLoading(false));
    }, [jobId]);

    const handleMarkComplete = async () => {
        try {
            await client.put(`/jobs/${jobId}/status`, { new_status_code: 'completed' });
            await loadBooking();
            if (onJobChanged) onJobChanged();
        } catch (err) { setError(err.response?.data?.error || 'Failed to update status.'); }
    };

    const handlePromoteToPortfolio = async (photo) => {
        try {
            await client.post(`/photos/portfolio/promote/${photo.photo_id}`);
            alert('Added to your portfolio!');
        } catch (err) { setError(err.response?.data?.error || 'Failed to add to portfolio.'); }
    };

    if (loading) return <div style={styles.loading}>Loading...</div>;
    if (!booking) return <div style={styles.loading}>{error}</div>;

    const hasGardenerReviewed = reviews.some(r => r.reviewer_role === 'gardener');
    const allPhotos     = booking.photos || [];
    const postingPhotos = allPhotos.filter(p => !p.photo_type || p.photo_type === 'job_posting');
    const beforePhotos  = allPhotos.filter(p => p.photo_type === 'before');
    const afterPhotos   = allPhotos.filter(p => p.photo_type === 'after');
    const canUploadPhotos = ['accepted','in_progress','completed','reviewed'].includes(booking.status_code);

    // Build toolbar actions
    const toolbarActions = [
        booking.status_code === 'accepted' && { label: '✓ Mark as Completed', onClick: handleMarkComplete, style: 'info' },
        booking.status_code === 'completed' && !hasGardenerReviewed &&
            { label: '⭐ Leave a Review', href: `/gardener/job/${jobId}/review`, style: 'primary' },
    ].filter(Boolean);

    return (
        <div style={embedded ? {} : styles.page}>
            {!embedded && (
                <div style={styles.backRow}>
                    <Link to="/gardener/bookings" style={styles.back}>← Back to My Bookings</Link>
                </div>
            )}

            <DetailToolbar actions={toolbarActions} />

            {error && <div style={styles.error}>{error}</div>}

            {/* Header */}
            <div style={styles.header}>
                <h2 style={styles.title}>{booking.title} <span style={styles.jobIdTag}>#{booking.job_id}</span></h2>
                <div style={styles.metaRow}>
                    <span style={statusPill(booking.status_code)}>{gardenerStatusLabels[booking.status_code] || booking.status_code}</span>
                    <span style={styles.metaItem}>📍 {booking.suburb_name} {booking.postcode}</span>
                    {booking.preferred_date && <span style={styles.metaItem}>📅 {new Date(booking.preferred_date).toLocaleDateString()}</span>}
                </div>
            </div>

            <div style={styles.grid}>
                {/* Left column */}
                <div>
                    <div style={card}>
                        <h3 style={cardHeader}>Job Description</h3>
                        <p style={styles.desc}>{booking.description}</p>
                    </div>

                    {booking.skills?.length > 0 && (
                        <div style={card}>
                            <h3 style={cardHeader}>Skills Required</h3>
                            <div style={styles.chips}>
                                {booking.skills.map(s => <span key={s.skill_id} style={styles.chip}>{s.skill_name}</span>)}
                            </div>
                        </div>
                    )}

                    {/* Buyer's job posting photos — read only for gardener */}
                    {postingPhotos.length > 0 && (
                        <div style={card}>
                            <h3 style={cardHeader}>Job Photos (from buyer)</h3>
                            <div style={styles.photoGrid}>
                                {postingPhotos.map(p => <img key={p.photo_id} src={p.storage_url} alt="" style={styles.photo} onError={e => e.target.style.display='none'} />)}
                            </div>
                        </div>
                    )}

                    {/* Before photos — gardener uploads */}
                    {canUploadPhotos && (
                        <div style={card}>
                            <h3 style={cardHeader}>Before Photos</h3>
                            <PhotoUploader
                                uploadUrl={`/photos/job/${jobId}`}
                                extraFields={{ photo_type: 'before' }}
                                existingPhotos={beforePhotos}
                                onDeleteUrl={p => `/photos/job/${p.photo_id}`}
                                onChange={loadBooking}
                                label="" maxFiles={6}
                            />
                            {beforePhotos.length > 0 && (
                                <div style={styles.promoteRow}>
                                    {beforePhotos.map(p => (
                                        <button key={p.photo_id} onClick={() => handlePromoteToPortfolio(p)} style={styles.promoteBtn}>
                                            + Add to portfolio
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* After photos — gardener uploads */}
                    {canUploadPhotos && (
                        <div style={card}>
                            <h3 style={cardHeader}>After Photos</h3>
                            <PhotoUploader
                                uploadUrl={`/photos/job/${jobId}`}
                                extraFields={{ photo_type: 'after' }}
                                existingPhotos={afterPhotos}
                                onDeleteUrl={p => `/photos/job/${p.photo_id}`}
                                onChange={loadBooking}
                                label="" maxFiles={6}
                            />
                            {afterPhotos.length > 0 && (
                                <div style={styles.promoteRow}>
                                    {afterPhotos.map(p => (
                                        <button key={p.photo_id} onClick={() => handlePromoteToPortfolio(p)} style={styles.promoteBtn}>
                                            + Add to portfolio
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <ReviewsTable reviews={reviews} currentUserId={user?.user_id} />
                </div>

                {/* Right column */}
                <div>
                    {/* Customer contact card */}
                    <div style={styles.contactCard}>
                        <h3 style={styles.cardHeaderGreen}>👤 Customer Details</h3>
                        <p style={styles.customerName}>{booking.buyer_name}</p>
                        {booking.buyer_phone && <p style={styles.contactRow}>📞 {booking.buyer_phone}</p>}
                        {booking.buyer_email && <p style={styles.contactRow}>✉️ {booking.buyer_email}</p>}
                        <p style={styles.contactHint}>
                            Contact the customer directly to confirm timing before the job.
                        </p>
                    </div>

                    {/* Accepted quote summary */}
                    <div style={card}>
                        <h3 style={cardHeader}>Your Accepted Quote</h3>
                        <div style={styles.quotePrice}>${booking.quoted_price}</div>
                        <div style={{display:'flex', gap:'12px', fontSize:'0.85rem', color:colors.textMuted, marginBottom:'8px'}}>
                            {booking.estimated_hours && <span>⏱ ~{booking.estimated_hours}hrs</span>}
                            {booking.proposed_date && <span>📅 {new Date(booking.proposed_date).toLocaleDateString()}</span>}
                        </div>
                        {booking.quote_notes && (
                            <p style={{fontSize:'0.86rem', color:colors.textMuted, fontStyle:'italic', borderLeft:`3px solid ${colors.brandLight}`, paddingLeft:'10px', margin:'8px 0'}}>
                                {booking.quote_notes}
                            </p>
                        )}
                        {booking.accepted_at && (
                            <p style={{fontSize:'0.78rem', color:colors.textFaint, marginTop:'8px'}}>
                                Accepted on {new Date(booking.accepted_at).toLocaleDateString()}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

const styles = {
    page:         { maxWidth:'1100px', margin:'0 auto', padding:'32px 24px' },
    loading:      { textAlign:'center', padding:'60px', color:colors.textMuted },
    backRow:      { marginBottom:'16px' },
    back:         { color:colors.brand, fontSize:'0.9rem' },
    error:        { backgroundColor:'#fdecea', border:'1px solid #f5c2bb', borderRadius:radius.sm, padding:'10px', color:'#c0392b', marginBottom:'14px', fontSize:'0.88rem' },
    header:       { marginBottom:space.lg },
    title:        { fontSize:'1.4rem', fontWeight:700, color:colors.text, marginBottom:'10px' },
    jobIdTag:     { fontSize:'0.85rem', color:colors.textFaint, fontWeight:400 },
    metaRow:      { display:'flex', gap:'10px', flexWrap:'wrap', alignItems:'center' },
    metaItem:     { color:colors.textMuted, fontSize:'0.85rem' },
    grid:         { display:'grid', gridTemplateColumns:'1fr 1fr', gap:space.md },
    desc:         { color:colors.textMuted, lineHeight:'1.6', fontSize:'0.92rem' },
    chips:        { display:'flex', flexWrap:'wrap', gap:'8px' },
    chip:         { padding:'5px 13px', borderRadius:radius.pill, backgroundColor:colors.brandLight, color:colors.brand, fontSize:'0.8rem', fontWeight:500 },
    photoGrid:    { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'8px' },
    photo:        { width:'100%', height:'80px', objectFit:'cover', borderRadius:radius.sm },
    promoteRow:   { display:'flex', flexWrap:'wrap', gap:'6px', marginTop:'10px' },
    promoteBtn:   { fontSize:'0.74rem', padding:'5px 10px', borderRadius:radius.sm, border:`1px solid ${colors.brand}`, backgroundColor:'white', color:colors.brand, cursor:'pointer' },
    contactCard:  { background:'#f4fbf6', border:'1px solid #c7e8d1', borderRadius:radius.lg, padding:space.lg, marginBottom:space.md },
    cardHeaderGreen: { fontSize:'0.85rem', fontWeight:700, color:colors.brand, marginBottom:'10px' },
    customerName: { fontWeight:700, fontSize:'1.05rem', color:colors.text, marginBottom:'8px' },
    contactRow:   { fontSize:'0.88rem', color:colors.textMuted, marginBottom:'4px' },
    contactHint:  { fontSize:'0.8rem', color:colors.textFaint, marginTop:'10px', fontStyle:'italic' },
    quotePrice:   { fontSize:'1.6rem', fontWeight:700, color:colors.brand, marginBottom:'8px' },
};
